/**
 * How a chat id becomes a gateway session key.
 *
 * openclaw generates real chat titles — "BTC Perp Weekly PnL Check" rather
 * than the raw text of the first message — with its cheap
 * `agents.defaults.utilityModel`, and publishes the result as the session
 * row's `displayName`. It does that only for sessions whose gateway key
 * carries the `dashboard:` scope its own Control UI uses. The terminal minted
 * unscoped keys, so it got `derivedTitle` (the first message verbatim) and
 * nothing else — which is why a chat whose first message was a `/model`
 * command the client sent itself ended up named after the command.
 *
 * So the scope is applied HERE, at the wire boundary, and only to chats
 * minted by a client that knows about it. That last part is the whole reason
 * this is a mapping rather than a change to how ids are generated:
 *
 *   The gateway session key IS the transcript identity now. Re-keying a chat
 *   that already exists would point it at a session openclaw has never heard
 *   of — an empty conversation, with the real one still on disk under the old
 *   key and nothing in the UI able to reach it. Existing chats therefore keep
 *   their unscoped ids and their unscoped keys, forever, and never get a
 *   generated title (openclaw titles a session from its FIRST user message
 *   and bails afterwards, so there is nothing to catch up on either).
 *
 * A new chat is marked instead: its id carries `dash-`, and that marker is
 * what `toGatewaySessionKey` keys off. The marker lives in the id — not the
 * scope itself — because the id is also a localStorage key and a path segment
 * in the sidecar's transcript routes, and `dash-<uuid>` is safe in both where
 * `dashboard:<uuid>` is not.
 */

/**
 * The scope openclaw generates titles for. A literal, matched exactly: the
 * gateway derives no meaning from the id inside it.
 */
export const GATEWAY_TITLE_SCOPE = 'dashboard:';

/**
 * Marker on chat ids minted by a client that scopes its keys.
 *
 * `dash-` because that is what this branch minted first, and nothing
 * constrains it beyond agreement between clients from here on. The comment
 * here used to cite PR #73 as a compatibility requirement; that is not true —
 * `git log --all -S'SCOPED_CHAT_ID_MARKER'` attributes the marker solely to
 * this branch, and `main` has never minted a chat id carrying it.
 *
 * Which also settles the failure that would otherwise be worth worrying
 * about: if unmarked ids HAD shipped, `toGatewaySessionKey` would re-key them
 * and orphan exactly the transcripts this file exists to protect. None did,
 * so there is nothing to orphan. What remains true is the reason to leave the
 * name alone now: two clients disagreeing about the marker is a split
 * transcript.
 */
export const SCOPED_CHAT_ID_MARKER = 'dash-';

/** Whether this chat id was minted with title-scoped keys. */
export function isScopedChatId(sessionId: string | undefined): boolean {
  return sessionId != null && sessionId.startsWith(SCOPED_CHAT_ID_MARKER);
}

/**
 * The gateway key for a chat id.
 *
 * A no-op for everything that is not a marked chat id — an unmarked (legacy)
 * id, an already-qualified `agent:main:…` key, the agent's own main session
 * key. That is what makes it safe to apply blindly to every outgoing
 * `sessionKey` / `key` param rather than at each call site by hand.
 */
export function toGatewaySessionKey(sessionId: string): string {
  return isScopedChatId(sessionId)
    ? `${GATEWAY_TITLE_SCOPE}${sessionId}`
    : sessionId;
}

/**
 * The chat id inside a gateway key, with the title scope removed.
 *
 * Only strips the scope when what follows is a marked id: a session someone
 * genuinely named `dashboard:<something-else>` (openclaw's own Control UI
 * does exactly that) is not one of our chats, and must keep its colon so
 * `isTerminalChatRow` still recognises it as foreign.
 */
export function stripTitleScope(key: string): string {
  if (!key.startsWith(GATEWAY_TITLE_SCOPE)) return key;
  const inner = key.slice(GATEWAY_TITLE_SCOPE.length);
  return isScopedChatId(inner) ? inner : key;
}
